import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { UserRole } from "@ontime/types";

interface AuthState {
  idToken: string | null;
  email: string | null;
  expiryTime: number | null;
  roles: UserRole[];
}

const initialState: AuthState = (() => {
  const savedAuth = localStorage.getItem('authData');
  if (savedAuth) {
    try {
      return JSON.parse(savedAuth) as AuthState;
    } catch {
      return { idToken: null, email: null, expiryTime: null, roles: [] };
    }
  }
  return { idToken: null, email: null, expiryTime: null, roles: [] };
})();

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (
      state,
      action: PayloadAction<{ idToken: string; expiryTime: number; roles: UserRole[] }>
    ) => {
      state.idToken = action.payload.idToken;
      state.expiryTime = action.payload.expiryTime;
      state.roles = action.payload.roles;
      localStorage.setItem('authData', JSON.stringify(state));
    },
    setUserName: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
      localStorage.setItem('authData', JSON.stringify(state));
    },
    clearToken: (state) => {
      state.idToken = null;
      state.expiryTime = null;
      state.roles = [];
      localStorage.setItem('authData', JSON.stringify(state));
    },
    clearUserName: (state) => {
      state.email = null;
      localStorage.setItem('authData', JSON.stringify(state));
    },

    // ---- NEW ROLE ACTIONS ----
    addRole: (state, action: PayloadAction<UserRole>) => {
      state.roles.push(action.payload);
      localStorage.setItem('authData', JSON.stringify(state));
    },
    updateRole: (
      state,
      action: PayloadAction<{ organizationId: string; newRole: string }>
    ) => {
      state.roles = state.roles.map((r) =>
        r.organizationId === action.payload.organizationId
          ? { ...r, role: action.payload.newRole }
          : r
      );
      localStorage.setItem('authData', JSON.stringify(state));
    },
    deleteRole: (state, action: PayloadAction<string>) => {
      state.roles = state.roles.filter((r) => r.organizationId !== action.payload);
      localStorage.setItem('authData', JSON.stringify(state));
    },
  },
});

export const {
  setToken,
  clearToken,
  setUserName,
  clearUserName,
  addRole,
  updateRole,
  deleteRole,
} = authSlice.actions;

export default authSlice.reducer;

export const selectAuth = (state: RootState) => state.auth;